import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';

const PRIORITIES = [
  { value: 'low', label: 'Low', color: '#6b7280' },
  { value: 'normal', label: 'Normal', color: '#3b82f6' },
  { value: 'high', label: 'High', color: '#f59e0b' },
  { value: 'urgent', label: 'Urgent', color: '#ef4444' },
];

export default function CreateWorkflowItemPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [workflowId, setWorkflowId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('normal');
  const [owners, setOwners] = useState([]);
  const [supervisors, setSupervisors] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [tmpl, usrs] = await Promise.all([
          api.getWorkflowTemplates(),
          api.getUsers(),
        ]);
        setTemplates(tmpl || []);
        setUsers((usrs || []).filter(u => u.is_active));
        // Default to General workflow
        const general = tmpl?.find(t => t.is_default);
        if (general) setWorkflowId(String(general.id));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return alert('Title is required');
    setSaving(true);
    try {
      const item = await api.createWorkflowItem({
        workflow_id: workflowId ? Number(workflowId) : undefined,
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
        owners: owners.map(Number),
        supervisors: supervisors.map(Number),
      });
      navigate(`/workflow/items/${item.id}`);
    } catch (err) {
      alert(err.message || 'Failed to create item');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</div>;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 600 }}>
      <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 13, marginBottom: 12, padding: 0 }}>
        ← Back
      </button>

      <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: '0 0 20px' }}>New Item</h1>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={{ fontWeight: 600, fontSize: 13, color: '#374151', display: 'block', marginBottom: 4 }}>Workflow</label>
          <select className="sales-input" value={workflowId} onChange={e => setWorkflowId(e.target.value)} style={{ width: '100%' }}>
            {templates.map(t => <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (Default)' : ''}</option>)}
          </select>
        </div>

        <div>
          <label style={{ fontWeight: 600, fontSize: 13, color: '#374151', display: 'block', marginBottom: 4 }}>Title *</label>
          <input className="sales-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="What needs to be done?" style={{ width: '100%' }} required />
        </div>

        <div>
          <label style={{ fontWeight: 600, fontSize: 13, color: '#374151', display: 'block', marginBottom: 4 }}>Description</label>
          <textarea className="sales-input" value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder="Additional details..." style={{ width: '100%', resize: 'vertical' }} />
        </div>

        <div>
          <label style={{ fontWeight: 600, fontSize: 13, color: '#374151', display: 'block', marginBottom: 8 }}>Priority</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {PRIORITIES.map(p => (
              <button key={p.value} type="button" onClick={() => setPriority(p.value)}
                style={{
                  flex: 1, padding: '8px 12px', borderRadius: 8, border: '2px solid',
                  borderColor: priority === p.value ? p.color : '#e5e7eb',
                  backgroundColor: priority === p.value ? p.color + '15' : '#fff',
                  color: priority === p.value ? p.color : '#6b7280',
                  fontWeight: 600, fontSize: 13, cursor: 'pointer',
                }}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label style={{ fontWeight: 600, fontSize: 13, color: '#374151', display: 'block', marginBottom: 4 }}>
            Assign Owners <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional, multi-select)</span>
          </label>
          <select className="sales-input" value="" onChange={e => {
            if (e.target.value && !owners.includes(e.target.value)) setOwners([...owners, e.target.value]);
            e.target.value = '';
          }} style={{ width: '100%' }}>
            <option value="">Select user to add...</option>
            {users.filter(u => !owners.includes(String(u.id))).map(u => (
              <option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>
            ))}
          </select>
          {owners.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              {owners.map(id => {
                const user = users.find(u => String(u.id) === id);
                return (
                  <span key={id} style={{
                    padding: '3px 10px', borderRadius: 12, backgroundColor: '#dbeafe', color: '#1d4ed8',
                    fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                    {user?.full_name || `#${id}`}
                    <button type="button" onClick={() => setOwners(owners.filter(o => o !== id))}
                      style={{ background: 'none', border: 'none', color: '#1d4ed8', cursor: 'pointer', fontSize: 14, padding: 0 }}>×</button>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <label style={{ fontWeight: 600, fontSize: 13, color: '#374151', display: 'block', marginBottom: 4 }}>
            Supervisors <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span>
          </label>
          <select className="sales-input" value="" onChange={e => {
            if (e.target.value && !supervisors.includes(e.target.value)) setSupervisors([...supervisors, e.target.value]);
            e.target.value = '';
          }} style={{ width: '100%' }}>
            <option value="">Select supervisor to add...</option>
            {users.filter(u => !supervisors.includes(String(u.id))).map(u => (
              <option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>
            ))}
          </select>
          {supervisors.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              {supervisors.map(id => {
                const user = users.find(u => String(u.id) === id);
                return (
                  <span key={id} style={{
                    padding: '3px 10px', borderRadius: 12, backgroundColor: '#fef3c7', color: '#92400e',
                    fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                    {user?.full_name || `#${id}`}
                    <button type="button" onClick={() => setSupervisors(supervisors.filter(s => s !== id))}
                      style={{ background: 'none', border: 'none', color: '#92400e', cursor: 'pointer', fontSize: 14, padding: 0 }}>×</button>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, paddingTop: 8 }}>
          <button type="button" className="btn" onClick={() => navigate(-1)} style={{ background: '#f3f4f6', color: '#374151' }}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}
            style={{ backgroundColor: '#D4A843', color: '#1a1a2e', fontWeight: 600, flex: 1 }}>
            {saving ? 'Creating...' : 'Create Item'}
          </button>
        </div>
      </form>
    </div>
  );
}
